/**
 * Comp `.rail` — the left column of the simplified landing: brand, the conversation search (at the top, above the
 * module scope: owner 2026-11-01), module scope, the two primary actions, pinned dashboards, conversations, footer.
 *
 * It collapses to an icon rail like the detailed sidebar (owner 2026-10-31), with the same toggle and the same saved
 * preference (`useUIStore.sidebarOpen`, `ShellSidebar.tsx`). Collapsed, each primary action keeps its icon with its
 * name in a tooltip; the dashboard and conversation icons open the rail on their list.
 *
 * Nothing here navigates to a builder. "Waiting on me" and "+" both say
 * something into a conversation; the agent does the rest.
 */
import React, { useState } from 'react';
import { Circle, LayoutDashboard, MessagesSquare, Plus } from 'lucide-react';
import { BrandMark } from '../../components/shell/BrandMark.js';
import { SidebarExpandedIcon } from '../../components/shell/shell-icons.js';
import { Button } from '../../components/ui/Button.js';
import { SearchBar } from '../../components/ui/SearchBar.js';
import { Tooltip } from '../../components/ui/Tooltip.js';
import { useWaitingOnMe } from '../../hooks/useWaitingOnMe.js';
import { SimpleModuleSelect } from './SimpleModuleSelect.js';
import { SimpleDashboardsSection } from './SimpleDashboardsSection.js';
import { SimpleConversationList } from './SimpleConversationList.js';
import { SimpleRailFooter } from './SimpleRailFooter.js';
import { railRowClass } from './rail-row.js';

interface Props {
  activeModuleId: string;
  selectedConversationId: string | null;
  selectedDashboardId: string | null;
  onNewConversation: () => void;
  onSelectConversation: (conversationId: string) => void;
  onSelectDashboard: (dashboardId: string) => void;
  onSay: (message: string) => void;
  /** The rail shows its lists; collapsed it is a column of icons. */
  expanded: boolean;
  onToggle: () => void;
}

export const SimpleRail: React.FC<Props> = ({
  activeModuleId,
  selectedConversationId,
  selectedDashboardId,
  onNewConversation,
  onSelectConversation,
  onSelectDashboard,
  onSay,
  expanded,
  onToggle,
}) => {
  const [search, setSearch] = useState('');
  const { data: waiting } = useWaitingOnMe(activeModuleId);
  const waitingCount = waiting?.count ?? 0;
  const askWaiting = () => onSay('What is waiting on me?');

  const toggle = (
    <Button
      variant="ghost"
      size="sm"
      iconOnly
      onClick={onToggle}
      aria-label={expanded ? 'Collapse sidebar' : 'Expand sidebar'}
    >
      <SidebarExpandedIcon />
    </Button>
  );

  if (!expanded) {
    const icon = (label: string, onClick: () => void, glyph: React.ReactNode, badge?: number) => (
      <Tooltip label={badge ? `${label} (${badge})` : label}>
        <Button
          variant="ghost"
          iconOnly
          onClick={onClick}
          aria-label={badge ? `${label} (${badge})` : label}
          className="relative"
        >
          {glyph}
          {badge ? (
            <span className="absolute right-1 top-1 grid min-w-3.5 place-items-center rounded-full bg-warning px-0.5 font-mono text-[9px] leading-3.5 text-heading">
              {badge}
            </span>
          ) : null}
        </Button>
      </Tooltip>
    );
    return (
      <aside className="flex min-h-0 flex-col items-center gap-1 overflow-y-auto border-r bg-sidebar px-2 py-4">
        <BrandMark withText={false} className="pb-2 pt-1" />
        {toggle}
        <div className="mt-3 flex flex-col items-center gap-1">
          {icon('New conversation', onNewConversation, <Plus size={18} aria-hidden />)}
          {icon('Waiting on me', askWaiting, <Circle size={16} aria-hidden />, waitingCount)}
          {icon('Dashboards', onToggle, <LayoutDashboard size={18} aria-hidden />)}
          {icon('Conversations', onToggle, <MessagesSquare size={18} aria-hidden />)}
        </div>
        <span className="flex-1" />
        <SimpleRailFooter selectedConversationId={selectedConversationId} expanded={false} />
      </aside>
    );
  }

  return (
    <aside className="flex min-h-0 flex-col overflow-y-auto border-r bg-sidebar px-3 py-4">
      <div className="flex items-center justify-between pb-3.5 pl-2 pt-1">
        <BrandMark />
        {toggle}
      </div>

      <div className="pb-1.5">
        <SearchBar value={search} onChange={setSearch} placeholder="Search conversations" className="sm:w-full" />
      </div>

      <SimpleModuleSelect />

      <div className="flex flex-col gap-0.5 pt-1">
        <Button variant="ghost" className={railRowClass({ className: 'text-body' })} onClick={onNewConversation}>
          <span className="grid size-4 flex-none place-items-center rounded-full bg-surface-alt text-heading">
            <Plus size={11} />
          </span>
          <span>New conversation</span>
        </Button>

        <Button variant="ghost" className={railRowClass({ className: 'text-body' })} onClick={askWaiting}>
          <Circle size={11} className="shrink-0 text-muted" />
          <span>Waiting on me</span>
          {waitingCount > 0 && <span className="ml-auto font-mono text-[10.5px] text-faint">{waitingCount}</span>}
        </Button>
      </div>

      <SimpleDashboardsSection
        activeModuleId={activeModuleId}
        selectedDashboardId={selectedDashboardId}
        onSelectDashboard={onSelectDashboard}
        onSay={onSay}
      />

      <SimpleConversationList
        activeModuleId={activeModuleId}
        search={search}
        selectedConversationId={selectedConversationId}
        onSelectConversation={onSelectConversation}
      />

      <SimpleRailFooter selectedConversationId={selectedConversationId} />
    </aside>
  );
};
